import { ChangeDetectionStrategy, Component, DestroyRef, OnDestroy, OnInit, computed, effect, inject, input, output, signal, untracked, viewChildren } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';

import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';

import { TestsService } from '@generated/services/tests.service';
import { ModelsService } from '@generated/services/models.service';
import { TestDto, TestType, CreateTestCommand, UpdateTestCommand, GetModelsHierarchyResult } from '@generated/models';
import { AgentModelVersionDto } from '@generated/models/agent-model-version-dto';
import { VoiceDto } from '@generated/models/voice-dto';
import { AgentMode } from '@generated/models/agent-mode';
import { E2ECallerDto } from '@generated/models/e-2-e-caller-dto';
import { E2ECallerSettings } from '@generated/models/e-2-e-caller-settings';
import { ChatMessageDto } from '@generated/models/chat-message-dto';
import { NotificationService } from '@core/services/notification.service';
import { AssistantContextService } from '@shared/services/assistant-context.service';
import { TesterSelection } from '@shared/components/model-tester/model-tester.component';
import { ModelSelectorButton } from '@shared/components/model-selector-button/model-selector-button';
import { OpenapiPropertiesEditorComponent } from '@shared/components/openapi-properties-editor/openapi-properties-editor';
import { ChatHistoryViewComponent } from '@shared/components/chat-history-view/chat-history-view.component';
import { RunTestDrawerComponent } from '@shared/components/run-test-drawer/run-test-drawer.component';
import { PropertyDefinition } from '@shared/models/property-schema';
import { Signalr } from '@core/services/signalr';
import { AssistantSurfaceDirective } from '@shared/directives/assistant-surface.directive';
import { AssistantEditableDirective } from '@shared/directives/assistant-editable.directive';
import { AssistantActionDirective } from '@shared/directives/assistant-action.directive';
import { TEST_FIELDS } from '@core/assistant/assistant-catalog';
import { AssistantUiService } from '@core/assistant/assistant-ui.service';
import { AssistantFieldOption, AssistantFieldSpec } from '@core/assistant/assistant.types';
import { AssistantAnchorDirective } from '@shared/directives/assistant-anchor.directive';

/**
 * Тип теста из ответа API → значение, по которому выбирается вкладка.
 *
 * Цифры в ключе не выбрасываем: `E2E` после удаления всего, кроме букв,
 * превращался в `ee`, такого ключа в карте нет — и сохранённый e2e-тест
 * каждый раз открывался вкладкой Simulation.
 *
 * Функция модуля, а не метод: так поведение закрыто тестом напрямую,
 * без поднятия компонента.
 */
export function normalizeTestType(value: unknown): TestType {
  const map: Record<string, TestType> = {
    simulation: 'Simulation',
    e2e: 'E2E',
    endtoend: 'E2E',
  };
  return map[String(value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '')] ?? 'Simulation';
}

@Component({
  selector: 'app-test-edit',
  standalone: true,
  imports: [
    AssistantActionDirective,
    AssistantSurfaceDirective,
    AssistantEditableDirective,
    AssistantAnchorDirective,
    FormsModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    TextareaModule,
    ToggleSwitchModule,
    SelectButtonModule,
    TooltipModule,
    ProgressSpinnerModule,
    TagModule,
    ModelSelectorButton,
    OpenapiPropertiesEditorComponent,
    ChatHistoryViewComponent,
    RunTestDrawerComponent
],
  templateUrl: './test-edit.component.html',
  styleUrl: './test-edit.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestEditComponent implements OnInit, OnDestroy {
  /** Идентификаторы полей для агента: опечатку ловит компилятор. */
  protected readonly F = TEST_FIELDS;
  private readonly assistantUi = inject(AssistantUiService);
  private readonly modelSelectors = viewChildren(ModelSelectorButton);
  private unregisterAssistantFields?: () => void;

  /**
   * Модели теста живут не в форме, а в своих диалогах — страница
   * объявляет их агенту сама. Симулированный собеседник и судья это
   * половина смысла теста: с бестолковой моделью «клиент» не давит, а
   * судья принимает любой ответ за успех.
   */
  private readonly assistantModelFields: AssistantFieldSpec[] = [
    {
      id: 'test.simulatedUserModel',
      surface: 'test',
      localId: 'simulatedUserModel',
      label: $localize`:@@tests.field.simulatedModel:Simulated user model`,
      kind: 'enum',
      optionsOf: () => this.llmVersionOptions(),
      allowUnlisted: true,
      read: () => this.test().simulated_user_model_version_id ?? '',
      write: value => this.onSimulatedUserModelSelected(String(value ?? '')),
      reveal: () => this.collapseModelSelectors(),
      available: () => !this.isCallerRealtime(),
    },
    {
      id: 'test.evaluationModel',
      surface: 'test',
      localId: 'evaluationModel',
      label: $localize`:@@tests.field.evalModel:Conversation evaluation model`,
      kind: 'enum',
      optionsOf: () => this.llmVersionOptions(),
      allowUnlisted: true,
      read: () => this.test().conversation_evaluation_model_version_id ?? '',
      write: value => this.onEvaluationModelSelected(String(value ?? '')),
      reveal: () => this.collapseModelSelectors(),
      available: () => this.useJudge(),
    },
    // Поля персоны — только у E2E, поэтому available завязан на тип: на
    // Simulation-тесте их нет, и агент не должен их предлагать. Модели
    // завязаны ещё и на режим: в Realtime нет пары STT+TTS, в Classic нет
    // speech-to-speech модели — предлагать скрытое поле значит писать
    // туда, откуда оно всё равно не уедет.
    {
      // Тип теста. Отдельный spec, а не snAssistantId на вкладке: вкладок
      // две, а поле одно — повесить на обе кнопки один id нельзя, а на
      // одну значило бы описать половину выбора. У созданного теста тип
      // неизменяем, поэтому available завязан на режим создания: предлагать
      // правку того, что заблокировано, — врать агенту.
      id: 'test.type',
      surface: 'test',
      localId: 'type',
      label: $localize`:@@tests.field.type:Test kind`,
      kind: 'enum',
      optionsOf: () => [
        { value: 'Simulation', label: 'Simulation' },
        { value: 'E2E', label: 'E2E' },
      ],
      read: () => this.test().type ?? 'Simulation',
      write: value => this.setType(value === 'E2E' ? 'E2E' : 'Simulation'),
      available: () => !this.isUpdateMode(),
    },
    {
      id: 'test.callerMode',
      surface: 'test',
      localId: 'callerMode',
      label: $localize`:@@tests.field.callerMode:Caller mode`,
      kind: 'enum',
      optionsOf: () => [
        { value: 'Classic', label: 'Classic' },
        { value: 'Realtime', label: 'Realtime' },
      ],
      read: () => this.test().caller?.mode ?? 'Classic',
      write: value => this.setCallerMode(value === 'Realtime' ? 'Realtime' : 'Classic'),
      available: () => this.isE2E(),
    },
    {
      id: 'test.callerRealtime',
      surface: 'test',
      localId: 'callerRealtime',
      label: $localize`:@@tests.field.callerRealtime:Caller speech-to-speech model`,
      kind: 'enum',
      optionsOf: () => this.versionOptions('realtime'),
      allowUnlisted: true,
      read: () => this.test().caller?.realtime_model_version_id ?? '',
      write: value => this.onCallerRealtimeModelSelected(String(value ?? '')),
      reveal: () => this.collapseModelSelectors(),
      available: () => this.isE2E() && this.isCallerRealtime(),
    },
    {
      // Голос персоны. Обязателен в Classic: воркер синтезирует речь по
      // его коду и без него падает на первой реплике. Раньше он
      // выбирался только внутри тестера модели — агент заполнял форму
      // целиком и всё равно не мог сохранить тест.
      id: 'test.callerVoice',
      surface: 'test',
      localId: 'callerVoice',
      label: $localize`:@@tests.field.callerVoice:Caller voice`,
      kind: 'enum',
      optionsOf: () => this.callerVoiceOptions(),
      allowUnlisted: true,
      read: () => this.test().caller?.voice_id ?? '',
      write: value => this.setCallerField('voice_id', String(value ?? '') || null),
      available: () => this.isE2E(),
    },
    {
      id: 'test.callerStt',
      surface: 'test',
      localId: 'callerStt',
      label: $localize`:@@tests.field.callerStt:Caller speech-to-text model`,
      kind: 'enum',
      optionsOf: () => this.versionOptions('stt'),
      allowUnlisted: true,
      read: () => this.test().caller?.stt_model_version_id ?? '',
      write: value => this.onCallerSttModelSelected(String(value ?? '')),
      reveal: () => this.collapseModelSelectors(),
      available: () => this.isE2E() && !this.isCallerRealtime(),
    },
    {
      id: 'test.callerTts',
      surface: 'test',
      localId: 'callerTts',
      label: $localize`:@@tests.field.callerTts:Caller text-to-speech model`,
      kind: 'enum',
      optionsOf: () => this.versionOptions('tts'),
      allowUnlisted: true,
      read: () => this.test().caller?.tts_model_version_id ?? '',
      write: value => this.onCallerTtsModelSelected(String(value ?? '')),
      reveal: () => this.collapseModelSelectors(),
      available: () => this.isE2E() && !this.isCallerRealtime(),
    },
  ];

  /**
   * Свернуть раскрытые таблицы моделей перед предложением.
   *
   * На новом тесте модель не выбрана, и селектор сразу раскрыт во весь
   * экран. Предложение агента в таком виде не видно: подсветка обводит
   * таблицу, а карточка «Применить» встаёт под ней — за краем экрана.
   */
  private collapseModelSelectors(): void {
    for (const selector of this.modelSelectors()) selector.collapse();
  }

  /** Версии LLM из уже загруженной иерархии, подписанные по-человечески. */
  private llmVersionOptions(): AssistantFieldOption[] {
    return this.versionOptions('llm');
  }

  /**
   * Голоса выбранной модели — для предложений ассистента.
   *
   * Голос выбирается в тестере внутри селектора модели, и отдельного
   * контрола на форме нет. Но поле обязательно для Classic, а значение
   * — ссылка на справочник: без списка агент не смог бы его назвать.
   * Поэтому список держим здесь, а грузим только когда версия модели
   * выбрана и поменялась.
   */
  private readonly callerVoices = signal<VoiceDto[]>([]);

  private callerVoiceOptions(): AssistantFieldOption[] {
    return this.callerVoices()
      .filter(v => !!v.id)
      .map(v => ({ value: v.id!, label: v.name ?? v.code ?? v.id! }));
  }

  private versionOptions(typeCode: string): AssistantFieldOption[] {
    const type = this.modelsHierarchy()?.model_types?.find(t => t.code === typeCode);
    const out: AssistantFieldOption[] = [];
    for (const gateway of type?.gateways ?? []) {
      for (const model of gateway.models ?? []) {
        for (const version of model.versions ?? []) {
          if (!version.id || version.is_active === false) continue;
          const label = [gateway.code, model.code, version.version].filter(Boolean).join(' / ');
          out.push({ value: version.id, label: version.is_recommended ? `${label} ★` : label });
        }
      }
    }
    return out;
  }

  private testsService = inject(TestsService);
  private modelsService = inject(ModelsService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private notificationService = inject(NotificationService);
  private assistantContext = inject(AssistantContextService);
  private signalr = inject(Signalr);
  private destroyRef = inject(DestroyRef);

  readonly isEmbedded = input<boolean>(false);
  readonly testIdInput = input<string | null>(null);

  readonly testSaved = output<{ id: string; name: string }>();
  readonly cancelled = output<void>();

  test = signal<TestDto>(this.defaultTest());
  testId = signal<string | null>(null);
  isLoading = signal(false);
  isSaving = signal(false);
  modelsHierarchy = signal<GetModelsHierarchyResult | null>(null);
  modelsIncludeAll = signal(false);

  // Run-test drawer state
  runDrawerVisible = signal(false);
  runTestId = signal<string | null>(null);
  runTestName = signal<string>('');
  // Standalone variables — НЕ привязаны к тексту scenario/criteria, юзер
  // добавляет руками через OpenapiPropertiesEditor. На load/save конвертим
  // в Test.dynamic_variables (Record<string,string>) через DynamicVariablesService.
  localVarProps = signal<PropertyDefinition[]>([]);

  isUpdateMode = computed(() => this.testId() !== null);

  /** Snapshot'ы выбранных на тесте моделей — пробрасываем в model-selector
   *  через [includeVersionSnapshots] чтобы deprecated / not_recommended
   *  модели, которых больше нет в иерархии, всё равно отрисовывались в
   *  drawer'е. */
  readonly simulatedUserSnapshots = computed<AgentModelVersionDto[]>(() => {
    const v = this.test().simulated_user_model_version;
    return v?.id ? [v] : [];
  });
  readonly evaluationSnapshots = computed<AgentModelVersionDto[]>(() => {
    const v = this.test().conversation_evaluation_model_version;
    return v?.id ? [v] : [];
  });

  // NextReply/ToolInvocation реализованы на бэке как заглушки — в UI
  // скрываем до готовности. E2E (SKN-468) — полный прогон настоящей
  // сессией: комната, аудио, запись, списание; роль клиента играет
  // машина со своим STT/LLM/TTS.
  readonly typeLockedHint = $localize`:@@tests.type.locked:The kind is set when the test is created. Create a new test to use the other kind.`;

  readonly typeTabs: Array<{ value: TestType; label: string; alpha?: boolean }> = [
    // Отдельные id, а не общие с фильтром списка: там метки короткие
    // («Симуляция», «E2E-прогон»), здесь — названия вкладок. Один id на
    // два разных текста означает один перевод на оба места, и в русском
    // одно из них оказывалось не тем.
    { value: 'Simulation', label: $localize`:@@tests.type.simulation.tab:Simulation test` },
    { value: 'E2E', label: $localize`:@@tests.type.e2e.tab:End-to-end test` },
  ];

  readonly isE2E = computed(() => this.test().type === 'E2E');

  /** Snapshot'ы STT/TTS персоны — та же причина, что у моделей выше. */
  readonly callerSttSnapshots = computed<AgentModelVersionDto[]>(() => {
    const v = this.test().caller?.stt_model_version;
    return v?.id ? [v] : [];
  });
  readonly callerTtsSnapshots = computed<AgentModelVersionDto[]>(() => {
    const v = this.test().caller?.tts_model_version;
    return v?.id ? [v] : [];
  });
  readonly callerRealtimeSnapshots = computed<AgentModelVersionDto[]>(() => {
    const v = this.test().caller?.realtime_model_version;
    return v?.id ? [v] : [];
  });

  readonly callerModeOptions = [
    { label: $localize`:@@tests.field.callerMode.classic:Classic`, value: 'Classic' },
    { label: $localize`:@@tests.field.callerMode.realtime:Realtime`, value: 'Realtime' },
  ];

  readonly isCallerRealtime = computed(() => this.test().caller?.mode === 'Realtime');

  /**
   * Версия модели, к которой привязан голос: TTS в Classic,
   * speech-to-speech модель в Realtime. Справочник голосов у провайдеров
   * свой на каждую модель, поэтому пикер без неё показать нечего.
   */
  readonly callerVoiceVersionId = computed(() =>
    this.isCallerRealtime()
      ? this.test().caller?.realtime_model_version_id ?? null
      : this.test().caller?.tts_model_version_id ?? null,
  );

  /**
   * Оценивает ли прогон судья.
   *
   * У Simulation он всегда есть: там разговор идёт текстом, и без оценки
   * критерия тест не утверждает ничего. У E2E отключается — тогда
   * вердиктом становится сам факт состоявшегося звонка.
   */
  readonly useJudge = computed(() => !this.isE2E() || (this.test().use_judge ?? true));

  /**
   * Какая панель видна на узком экране. На десктопе не используется:
   * там обе колонки на месте, а переключатель скрыт стилями.
   */
  readonly mobilePane = signal<'settings' | 'conversation'>('settings');

  saveLabel = computed(() => {
    if (this.isSaving()) return $localize`:@@common.saving:Saving…`;
    return this.isUpdateMode()
      ? $localize`:@@common.save:Save`
      : $localize`:@@tests.create:Create test`;
  });

  constructor() {
    // Голоса подтягиваем под выбранную модель — нужны ассистенту как
    // список допустимых значений обязательного поля.
    effect(() => {
      const versionId = this.callerVoiceVersionId();
      if (!versionId) {
        untracked(() => this.callerVoices.set([]));
        return;
      }
      untracked(() => {
        this.modelsService
          .apiModelsVersionsModelVersionIdVoicesGet({ modelVersionId: versionId })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: result => this.callerVoices.set(result.voices ?? []),
            error: () => this.callerVoices.set([]),
          });
      });
    });

    effect(() => {
      const t = this.test();
      if (!t.id) return;
      this.assistantContext.set({
        description: 'Test editor',
        value: { type: 'test', id: t.id, name: t.name, testType: t.type },
      });
    });
  }

  ngOnInit(): void {
    this.unregisterAssistantFields = this.assistantUi.registerFields(this.assistantModelFields);
    this.loadModelsHierarchy();
    const id = this.isEmbedded() ? this.testIdInput() : this.route.snapshot.paramMap.get('id');
    if (id) {
      this.testId.set(id);
      this.loadTest(id);
    } else if (!this.isEmbedded()) {
      // Create-mode: если перешли сюда через router.navigate('/tests/new', {state: {seedMessages}}),
      // предзаполняем seed_messages. Используется, например, кнопкой
      // «Создать тест отсюда» из conversation-detail'а — она передаёт
      // срез истории до кликнутого сообщения. history.state — синхронный
      // источник, доступен сразу после Router.navigate(); NavigationExtras.state
      // сохраняется туда фреймворком.
      const seed = (history.state?.seedMessages ?? null) as ChatMessageDto[] | null;
      if (Array.isArray(seed) && seed.length > 0) {
        this.test.update(t => ({ ...t, seed_messages: seed }));
      }
      // Тип нового теста задаёт рут: /tests/new/simulation и
      // /tests/new/e2e. Так у каждого типа свой адрес — его видит
      // ассистент, им можно поделиться и он остаётся в истории браузера.
      const routeType = this.route.snapshot.data?.['testType'] as TestType | undefined;
      if (routeType) this.applyType(routeType);
    }

    // Тест обновлён где-то ещё — если это текущий тест, синхронизируем модель.
    this.signalr.testUpdated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((dto) => {
        if (!dto?.id || dto.id !== this.testId()) return;
        this.test.set({ ...dto, type: this.normalizeTestType(dto.type) });
        this.localVarProps.set(this.dictToVarProps(dto.dynamic_variables ?? {}));
      });
  }

  ngOnDestroy(): void {
    this.unregisterAssistantFields?.();
    if (!this.isEmbedded()) this.assistantContext.clear();
  }

  private defaultTest(): TestDto {
    return {
      type: 'Simulation',
      name: '',
      description: '',
      simulated_user_scenario: '',
      success_criteria: '',
      max_conversation_turns: 5,
      mock_all_tools: false,
      use_judge: true,
      seed_messages: [],
      dynamic_variables: {},
    };
  }

  private loadModelsHierarchy(): void {
    this.modelsService.apiModelsHierarchyGet({ includeAll: this.modelsIncludeAll() }).subscribe({
      next: (hierarchy) => this.modelsHierarchy.set(hierarchy),
      error: () => this.notificationService.showError($localize`:@@tests.notify.modelsFail:Failed to load models hierarchy`),
    });
  }

  onModelsIncludeAllChange(includeAll: boolean): void {
    this.modelsIncludeAll.set(includeAll);
    this.loadModelsHierarchy();
  }

  onCallerSttModelSelected(versionId: string): void {
    this.setCallerField('stt_model_version_id', versionId || null);
  }

  onCallerTtsModelSelected(versionId: string): void {
    this.setCallerField('tts_model_version_id', versionId || null);
  }

  onCallerRealtimeModelSelected(versionId: string): void {
    this.setCallerField('realtime_model_version_id', versionId || null);
  }

  /**
   * Выбор из тестера: кроме версии он отдаёт голос, выбранный рядом с ней.
   * Пишем оба за одно обновление — иначе второй `setCallerField` прочитает
   * `t.caller` до применения первого и затрёт его.
   */
  onCallerTtsTesterSelected(selection: TesterSelection): void {
    this.patchCaller({
      tts_model_version_id: selection.versionId || null,
      voice_id: selection.voiceId,
    });
  }

  onCallerRealtimeTesterSelected(selection: TesterSelection): void {
    this.patchCaller({
      realtime_model_version_id: selection.versionId || null,
      voice_id: selection.voiceId,
    });
  }

  /**
   * Смена режима персоны. Голос гасим: он выбирался в справочнике,
   * привязанном к конкретной версии модели, и для модели другого режима
   * этого голоса попросту нет — сохранённый id указывал бы в пустоту.
   */
  setCallerMode(mode: AgentMode): void {
    this.patchCaller({ mode, voice_id: null });
  }

  onSimulatedUserModelSelected(versionId: string): void {
    this.test.update(t => ({ ...t, simulated_user_model_version_id: versionId || null }));
  }

  onEvaluationModelSelected(versionId: string): void {
    this.test.update(t => ({ ...t, conversation_evaluation_model_version_id: versionId || null }));
  }

  private normalizeTestType = normalizeTestType;

  private loadTest(id: string): void {
    this.isLoading.set(true);
    this.testsService.apiTestsIdGet({ id }).subscribe({
      next: (response: TestDto) => {
        this.test.set({ ...response, type: this.normalizeTestType(response.type) });
        this.localVarProps.set(this.dictToVarProps(response.dynamic_variables ?? {}));
        this.isLoading.set(false);
      },
      error: () => {
        this.notificationService.showError($localize`:@@tests.notify.loadFail:Failed to load test`);
        this.isLoading.set(false);
        if (!this.isEmbedded()) this.router.navigate(['/tests']);
      },
    });
  }

  setField<K extends keyof TestDto>(field: K, value: TestDto[K]): void {
    this.test.update(t => ({ ...t, [field]: value }));
  }

  /**
   * Клик по вкладке типа. У созданного теста тип менять нельзя — вкладки
   * там выключены, и сюда попасть неоткуда.
   *
   * Меняем не только модель, но и адрес: у каждого типа свой рут, и если
   * оставить в строке `/tests/new/simulation` при выбранном E2E, ссылка
   * начнёт врать — по ней откроется не то, что было на экране.
   * `replaceUrl`, потому что метание между вкладками — это не шаги, по
   * которым пользователь захочет ходить кнопкой «назад».
   */
  setType(type: TestType): void {
    if (this.isUpdateMode()) return;
    this.applyType(type);
    if (!this.isEmbedded()) {
      this.router.navigate(['/tests/new', type === 'E2E' ? 'e2e' : 'simulation'], {
        replaceUrl: true,
      });
    }
  }

  private applyType(type: TestType): void {
    this.test.update(t => ({
      ...t,
      type,
      // Персона осмысленна только у E2E, и бэкенд отвергает её на других
      // типах. Переключили тип — убираем, иначе сохранение упадёт
      // валидацией, а автор теста не поймёт, за что.
      caller: type === 'E2E' ? (t.caller ?? this.defaultCaller()) : null,
    }));
  }

  /** Дефолты персоны — те же, что у сущности на бэкенде. */
  private defaultCaller(): E2ECallerDto {
    return {
      persona: '',
      mode: 'Classic',
      stt_model_version_id: null,
      tts_model_version_id: null,
      realtime_model_version_id: null,
      voice_id: null,
      interrupt_agent: false,
      caller_speaks_first: false,
      max_duration_seconds: 180,
      silence_timeout_seconds: 20,
    };
  }

  /** Read-модель персоны → write-модель команды: наружу уезжают только
   *  id, развёрнутые версии моделей бэкенду не нужны. */
  private callerSettings(caller?: E2ECallerDto | null): E2ECallerSettings | null {
    if (!caller) return null;
    return {
      persona: caller.persona,
      mode: caller.mode ?? 'Classic',
      stt_model_version_id: caller.stt_model_version_id,
      tts_model_version_id: caller.tts_model_version_id,
      realtime_model_version_id: caller.realtime_model_version_id,
      voice_id: caller.voice_id,
      interrupt_agent: caller.interrupt_agent ?? false,
      caller_speaks_first: caller.caller_speaks_first ?? false,
      max_duration_seconds: caller.max_duration_seconds ?? 180,
      silence_timeout_seconds: caller.silence_timeout_seconds ?? 20,
    };
  }

  setCallerField<K extends keyof E2ECallerDto>(field: K, value: E2ECallerDto[K]): void {
    this.patchCaller({ [field]: value } as Partial<E2ECallerDto>);
  }

  /** Несколько полей персоны за одно обновление сигнала. */
  private patchCaller(patch: Partial<E2ECallerDto>): void {
    this.test.update(t => ({
      ...t,
      caller: { ...(t.caller ?? this.defaultCaller()), ...patch },
    }));
  }

  // Seed-сообщения (User/Assistant) полностью управляются общим
  // ChatHistoryViewComponent в editable-режиме — он эмитит обновлённый
  // массив через (messagesChange), который кладём в test.seed_messages.

  // ---- Dynamic variables ----
  // UI хранит OpenApiPropertiesSchema (тот же формат что agent.variables_open_api_schema),
  // потому что общий VariableTrackerDirective работает с этим типом.
  // На API уходит Record<string,string> — конвертим default-значения схемы.

  private dictToVarProps(dict: { [key: string]: string }): PropertyDefinition[] {
    return Object.entries(dict).map(([name, value]) => ({
      name,
      title: name,
      type: 'string',
      nullable: false,
      default: value,
    }));
  }

  private propsToVarDict(props: PropertyDefinition[]): { [key: string]: string } {
    const dict: { [key: string]: string } = {};
    for (const p of props) {
      if (!p.name?.trim()) continue;
      dict[p.name] = p.default == null ? '' : String(p.default);
    }
    return dict;
  }

  onVarPropertiesChange(props: PropertyDefinition[]): void {
    this.localVarProps.set(props);
  }

  validate(): string | null {
    const t = this.test();
    if (!t.name?.trim()) return $localize`:@@tests.validate.name:Name is required`;
    if (!t.type) return $localize`:@@tests.validate.type:Type is required`;
    // E2E переиспользует поля Simulation: сценарий — это цель звонка,
    // обе модели и критерий успеха нужны так же.
    if (t.type === 'Simulation' || t.type === 'E2E') {
      if (!t.simulated_user_scenario?.trim()) return $localize`:@@tests.validate.scenario:Simulated user scenario is required`;
      // Критерий и модель оценки нужны только судье. Его выключили —
      // спрашивать их не за чем, читать их будет некому.
      if (this.useJudge() && !t.success_criteria?.trim()) return $localize`:@@tests.validate.criteria:Success criteria is required`;
      // LLM персоны не нужна Realtime-клиенту: думает сама
      // speech-to-speech модель.
      if (!this.isCallerRealtime() && !t.simulated_user_model_version_id) return $localize`:@@tests.validate.simulatedModel:Simulated user model is required`;
      if (this.useJudge() && !t.conversation_evaluation_model_version_id) return $localize`:@@tests.validate.evalModel:Conversation evaluation model is required`;
    }
    if (t.type === 'E2E') {
      const c = t.caller;
      if (!c?.persona?.trim()) return $localize`:@@tests.validate.callerPersona:Caller persona is required`;
      if (this.isCallerRealtime()) {
        if (!c?.realtime_model_version_id) return $localize`:@@tests.validate.callerRealtime:Caller speech-to-speech model is required`;
      } else {
        // Без STT клиент не слышит агента, без TTS агенту нечего слушать —
        // прогон выродился бы в тишину с двух сторон.
        if (!c?.stt_model_version_id) return $localize`:@@tests.validate.callerStt:Caller speech-to-text model is required`;
        if (!c?.tts_model_version_id) return $localize`:@@tests.validate.callerTts:Caller text-to-speech model is required`;
        // Голос — не формальность: воркер синтезирует речь по его коду и
        // без него падает на первой реплике, а прогон выходит пустой
        // сессией без записи и причины.
        if (!c?.voice_id) return $localize`:@@tests.validate.callerVoice:Caller voice is required — pick it in the model tester next to the text-to-speech model`;
      }
    }
    return null;
  }

  save(afterSave?: (id: string, name: string) => void): void {
    const err = this.validate();
    if (err) {
      this.notificationService.showError(err);
      return;
    }
    const t = this.test();
    const editable = {
      type: t.type,
      name: t.name,
      description: t.description,
      simulated_user_scenario: t.simulated_user_scenario,
      success_criteria: t.success_criteria,
      max_conversation_turns: t.max_conversation_turns,
      mock_all_tools: t.mock_all_tools,
      use_judge: t.use_judge ?? true,
      seed_messages: t.seed_messages,
      simulated_user_model_version_id: t.simulated_user_model_version_id,
      conversation_evaluation_model_version_id: t.conversation_evaluation_model_version_id,
      dynamic_variables: this.propsToVarDict(this.localVarProps()),
      // Персона уходит только у E2E: на других типах бэкенд её отвергает.
      caller: t.type === 'E2E' ? this.callerSettings(t.caller) : null,
    };

    this.isSaving.set(true);
    if (this.isUpdateMode() && this.testId()) {
      const cmd: UpdateTestCommand = { ...editable, id: this.testId()! };
      this.testsService.apiTestsPut({ body: cmd }).subscribe({
        next: () => {
          this.notificationService.showSuccess($localize`:@@tests.notify.updated:Test updated`);
          this.isSaving.set(false);
          this.testSaved.emit({ id: this.testId()!, name: t.name ?? '' });
          if (afterSave) {
            afterSave(this.testId()!, t.name ?? '');
          } else if (!this.isEmbedded()) {
            this.router.navigate(['/tests']);
          }
        },
        error: () => {
          this.notificationService.showError($localize`:@@tests.notify.updateFail:Failed to update test`);
          this.isSaving.set(false);
        },
      });
    } else {
      const cmd: CreateTestCommand = editable;
      this.testsService.apiTestsPost({ body: cmd }).subscribe({
        next: (result) => {
          this.notificationService.showSuccess($localize`:@@tests.notify.created:Test created`);
          this.isSaving.set(false);
          const newId = result.id ?? '';
          this.testId.set(newId);
          this.test.update(test => ({ ...test, id: newId }));
          this.testSaved.emit({ id: newId, name: t.name ?? '' });
          if (afterSave) {
            afterSave(newId, t.name ?? '');
          } else if (!this.isEmbedded()) {
            this.router.navigate(['/tests']);
          }
        },
        error: () => {
          this.notificationService.showError($localize`:@@tests.notify.createFail:Failed to create test`);
          this.isSaving.set(false);
        },
      });
    }
  }

  cancel(): void {
    if (this.isEmbedded()) this.cancelled.emit();
    else this.router.navigate(['/tests']);
  }

  saveAndRun(): void {
    this.save((id, name) => {
      this.runTestId.set(id);
      this.runTestName.set(name);
      this.runDrawerVisible.set(true);
    });
  }
}
